import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2 } from "lucide-react";

import {
  getExplicitVisualPreference,
  saveExplicitVisualPreference,
} from "../../lib/brandStudioApi";

function errorText(error) {
  return error?.response?.data?.detail || "Не удалось сохранить визуальный приоритет.";
}

export default function ExplicitVisualPreferenceInlineV1() {
  const [status, setStatus] = useState("loading");
  const [savedNote, setSavedNote] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    getExplicitVisualPreference()
      .then((value) => {
        if (cancelled) return;
        const next = value?.note || "";
        setSavedNote(next);
        setNote(next);
        setStatus("ready");
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(errorText(requestError));
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const changed = useMemo(
    () => note.trim() !== savedNote,
    [note, savedNote],
  );

  const save = async () => {
    if (busy || !changed) return;
    setBusy(true);
    setError("");
    try {
      const result = await saveExplicitVisualPreference({ note: note.trim() });
      const next = result?.note || "";
      setSavedNote(next);
      setNote(next);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  if (status === "loading") {
    return (
      <section className="mt-8 rounded-[20px] border border-[#DCCDB8] bg-[#F6EFE5] p-5 md:p-6">
        <div className="inline-flex items-center gap-2 text-[12px] text-[#806B55]">
          <Loader2 size={14} className="animate-spin" /> Открываю визуальный приоритет…
        </div>
      </section>
    );
  }

  return (
    <section
      className="mt-8 rounded-[20px] border border-[#DCCDB8] bg-[#F6EFE5] p-5 md:p-6"
      data-testid="explicit-visual-preference-v1"
    >
      <div className="max-w-3xl">
        <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
          ЧТО ОСОБЕННО ВАЖНО
        </div>
        <h3 className="mt-2 font-serif text-[26px] leading-tight text-[#2B211A] md:text-[30px]">
          Есть решение, которое важнее частоты в референсах?
        </h3>
        <p className="mt-2 text-[12.5px] leading-5 text-[#655141]">
          Если какой-то цвет, материал, характер фотографии или графический приём
          для тебя принципиален — напиши это здесь. Я буду считать это твоим явным
          решением, а не случайной деталью изображения.
        </p>
      </div>

      <textarea
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
          setError("");
        }}
        maxLength={3000}
        rows={4}
        placeholder="Например: основной цвет — глубокий шоколадный; бежевые оттенки — поддерживающие; denim — только небольшой акцент."
        className="mt-4 w-full resize-y rounded-[14px] border border-[#DCCDB8] bg-white px-4 py-3 text-[13px] leading-6 text-[#3E2E20] outline-none focus:border-[#8C6C49]"
        data-testid="explicit-visual-preference-v1-note"
      />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {changed && (
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="inline-flex min-h-10 items-center gap-2 rounded-full bg-[#3D2A20] px-4 text-[12px] text-[#FFF8ED] disabled:opacity-45"
            data-testid="explicit-visual-preference-v1-save"
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            Сохранить как важный ориентир
          </button>
        )}
        {!changed && savedNote && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[11px] text-[#586044]"
            data-testid="explicit-visual-preference-v1-saved"
          >
            <Check size={12} /> Сохранено как явный приоритет
          </span>
        )}
      </div>

      {savedNote && !changed && (
        <p className="mt-3 text-[11.5px] leading-5 text-[#806B55]">
          Этот ориентир будет сильнее простой частоты цветов и деталей в референсах.
        </p>
      )}

      {error && (
        <div className="mt-4 rounded-[12px] border border-[#E2CFC8] bg-[#FFF8F4] px-3 py-2 text-[11.5px] text-[#6B443A]">
          {error}
        </div>
      )}
    </section>
  );
}
